
import React from 'react';
import { motion } from 'framer-motion';
import { UserCircle, Shield, Eye, Share2, Users, Link } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import PageTransition from '@/components/ui/PageTransition';
import { useToast } from '@/hooks/use-toast';

const Dashboard = () => {
  const { toast } = useToast();
  
  const createPublicProfile = async () => {
    try {
      // This is a placeholder - in a real implementation, we would:
      // 1. Fetch the user's biodata
      // 2. Create a new public profile with a random slug
      // 3. Set up default visibility for fields
      
      toast({
        title: "Public Profile Created",
        description: "Your public profile link has been generated.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create public profile. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const addPreApprovedContact = async () => {
    try {
      // This is a placeholder - in a real implementation, we would:
      // 1. Take phone number input from the user
      // 2. Add it to the pre_approved_contacts table
      
      toast({
        title: "Contact Added",
        description: "The contact has been added to your approved list.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add contact. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main className="flex-grow pt-28 pb-20">
          <div className="container px-6 mx-auto">
            <div className="max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                  <div>
                    <h1 className="text-3xl font-bold mb-2">Your Dashboard</h1>
                    <p className="text-muted-foreground">Manage your biodata profile and privacy settings</p>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <Button asChild className="rounded-full">
                      <a href="/profile">
                        <UserCircle className="mr-2 h-4 w-4" />
                        Edit Profile
                      </a>
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-muted">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
                      <UserCircle className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Profile Status</h3>
                    <p className="text-muted-foreground mb-4">Your profile is incomplete.</p>
                    <Button asChild variant="outline" size="sm" className="w-full">
                      <a href="/profile">Complete Profile</a>
                    </Button>
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-muted">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
                      <Shield className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Privacy Status</h3>
                    <p className="text-muted-foreground mb-4">Configure your privacy settings.</p>
                    <Button asChild variant="outline" size="sm" className="w-full">
                      <a href="/privacy">Manage Privacy</a>
                    </Button>
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-muted">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
                      <Eye className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Preview</h3>
                    <p className="text-muted-foreground mb-4">Preview how others see your profile.</p>
                    <Button asChild variant="outline" size="sm" className="w-full">
                      <a href="/preview">View Preview</a>
                    </Button>
                  </div>
                </div>
                
                {/* New row for Public Profile Links and Trusted Contacts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-muted">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
                      <Link className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Public Profile Links</h3>
                    <p className="text-muted-foreground mb-4">Create custom shareable links with control over what information is visible.</p>
                    <Button onClick={createPublicProfile} variant="outline" size="sm" className="w-full mb-2">
                      Create Public Link
                    </Button>
                    <Button asChild variant="ghost" size="sm" className="w-full">
                      <a href="/public-links">Manage Existing Links</a>
                    </Button>
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-muted">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Pre-Approved Contacts</h3>
                    <p className="text-muted-foreground mb-4">Add trusted contacts who will automatically be granted access to your private information.</p>
                    <Button onClick={addPreApprovedContact} variant="outline" size="sm" className="w-full mb-2">
                      Add Trusted Contact
                    </Button>
                    <Button asChild variant="ghost" size="sm" className="w-full">
                      <a href="/trusted-contacts">Manage Trusted Contacts</a>
                    </Button>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-6 border border-muted mb-10">
                  <h3 className="text-xl font-semibold mb-4">Share Your Profile</h3>
                  <p className="text-muted-foreground mb-6">Generate a custom link to share your profile with chosen privacy settings.</p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button asChild className="rounded-full">
                      <a href="/share">
                        <Share2 className="mr-2 h-4 w-4" />
                        Create Share Link
                      </a>
                    </Button>
                    <Button asChild variant="outline" className="rounded-full">
                      <a href="/share-history">View Share History</a>
                    </Button>
                  </div>
                </div>
                
                <div className="bg-secondary/50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-2">Getting Started</h3>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                    <li>Complete your profile information</li>
                    <li>Set privacy controls for each section</li>
                    <li>Preview how others will see your profile</li>
                    <li>Add trusted contacts for quick access approval</li>
                    <li>Create public links to share specific information</li>
                  </ol>
                </div>
              </motion.div>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    </PageTransition>
  );
};

export default Dashboard;
